import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, resolveDirective as _resolveDirective, createElementBlock as _createElementBlock, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "col"
}
const _hoisted_2 = { class: "d-flex flex-column flex-sm-row align-items-start justify-content-between gap-2" }
const _hoisted_3 = { class: "me-sm-3 flex-grow-1" }
const _hoisted_4 = ["id", "title"]
const _hoisted_5 = { class: "my-2" }
const _hoisted_6 = { class: "mb-2 ms-sm-auto" }
const _hoisted_7 = {
  key: 0,
  class: "mb-2"
}
const _hoisted_8 = ["title", "id"]
const _hoisted_9 = { class: "route" }
const _hoisted_10 = { class: "visually-hidden" }
const _hoisted_11 = { class: "route__body" }
const _hoisted_12 = { class: "route__title mb-1" }
const _hoisted_13 = {
  key: 0,
  class: "table-border mb-1"
}
const _hoisted_14 = { key: 2 }
const _hoisted_15 = { key: 3 }

import { ref, PropType, watchEffect, onMounted, onBeforeUnmount } from 'vue';
import {
  ComputeTaskUtils,
  DataPipelineTaskTabOptionType,
  DATA_PIPELINE_TASK_TAB_OPTIONS,
} from '@/utils/compute-utils';
import { ComputeTaskType } from '@/gql/graphql';
import {
  BBadge,
  BButton,
  BButtonGroup,
  BCard,
  BCollapse,
  BTable,
} from 'bootstrap-vue-next';
import { useUserStore } from '@/store/user';
import { IS_DEBUG_MODE, COMPUTE_TASK_STATUS } from '@/constants';
import ComputeTaskFilesList from '@/components/compute/ComputeTaskFilesList.vue';
import ComputeTaskStatusBadge from '@/components/compute/ComputeTaskStatusBadge.vue';

export type TaskFileType = {
  name: string;
  path: string;
  type: string;
  fileType: string;
  description: string;
};

// --- Sections for Sample Report ---
type ProgressRow = {
  label: string;
  value: unknown;
  format?: Intl.NumberFormatOptions; // optional override
  scale?: number; // multiply before formatting (e.g., 100 for percentage)
  suffix?: string; // e.g., ' %'
};

type ProgressSection = {
  key?: string;
  title: string;
  items: ProgressRow[];
  state?: string; // COMPUTE_TASK_STATUS
};


export default /*@__PURE__*/_defineComponent({
  __name: 'ComputeTaskDetail',
  props: {
  task: { type: Object as PropType<ComputeTaskType> },
  expanded: { type: Boolean, default: false },
  selectedTab: {
    type: String as PropType<DataPipelineTaskTabOptionType>,
    default: DATA_PIPELINE_TASK_TAB_OPTIONS.FILES,
  },
},
  emits: ["update:expanded", "update:selected-tab", "bucket-requested", "exec-details-requested", "debug-details-requested", "abort-requested", "file-preview-requested", "file-download-requested", "layout-needed"],
  setup(__props, { emit: __emit }) {

const userStore = useUserStore();

// --- Component Properties ---
const props = __props;

// --- Emits ---
const emits = __emit;

// helper to emit layout events with the task id
function emitLayout(phase: 'start' | 'end' | 'resize') {
  emits('layout-needed', phase, props.task?.id ?? null);
}

// Observe size changes (images/tables loading) and notify parent
const cardRef = ref<InstanceType<typeof BCard> | null>(null);
let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  const el = cardRef.value?.$el as HTMLElement | undefined;
  if (!el || typeof ResizeObserver === 'undefined') return;
  resizeObserver = new ResizeObserver(() => emitLayout('resize'));
  resizeObserver.observe(el);
});

onBeforeUnmount(() => resizeObserver?.disconnect());

// local reactive state ― decoupled from prop so the card can toggle itself
const selectedTab = ref(props.selectedTab);
const isExpanded = ref(props.expanded);
const detailedView = ref(true);

watchEffect(
  () => (
    (isExpanded.value = props.expanded),
    console.debug('isExpanded set to', isExpanded.value, 'from prop')
  )
);
watchEffect(
  () => (
    (selectedTab.value = props.selectedTab),
    console.debug('selectedTab set to', selectedTab.value, 'from prop')
  )
);

function selectTab(tab: DataPipelineTaskTabOptionType) {
  selectedTab.value = tab;
  emits('update:selected-tab', selectedTab.value);
}

function toggleCard() {
  isExpanded.value = !isExpanded.value;
  emits('update:expanded', isExpanded.value);
}

function expandCard() {
  if (!isExpanded.value) {
    toggleCard();
  }
}

// --- Files Output ---
function progressSectionState(
  status?: string
): 'pending' | 'active' | 'error' | 'done' {
  // initial validation of task and its status
  if (
    !status ||
    !props.task ||
    !props.task.status ||
    status === COMPUTE_TASK_STATUS.PENDING
  ) {
    return 'pending';
  }
  // running section is active if the task is running
  if (
    status === COMPUTE_TASK_STATUS.RUNNING ||
    status === COMPUTE_TASK_STATUS.CREATING
  ) {
    if (props.task.status === COMPUTE_TASK_STATUS.RUNNING) {
      return 'active';
    }
    return 'error';
  }
  // completed section is done if the task is completed
  if (status === COMPUTE_TASK_STATUS.COMPLETED) {
    return 'done';
  }
  // otherwise, the section is failed
  return 'error';
}

function taskProgress(sample: any): ProgressSection[] {
  const p = sample?.report?.progress || {};

  // helper to keep only defined values
  const rows = (entries: ProgressRow[]) =>
    entries.filter((e) => e.value !== undefined && e.value !== null);

  return [
    {
      key: 'transfer',
      title: 'Data',
      items: rows([
        { label: 'Reads total', value: p.transfer_data?.raw_total },
      ]),
      state: progressSectionState(p.transfer_data?.phase_status),
    },
    {
      key: 'extract',
      title: 'Extracted Barcodes',
      items: rows([
        {
          label: 'Extracted total',
          value: p.extract_barcodes?.extracted_total,
        },
        {
          label: 'Loss',
          value: p.extract_barcodes?.loss_raw_to_extracted,
        },
        {
          label: 'Extracted percentage',
          value: p.extract_barcodes?.extracted_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
      ]),
      state: progressSectionState(p.extract_barcodes?.phase_status),
    },
    {
      key: 'trim',
      title: 'Trimming',
      items: rows([
        {
          label: 'Trimmed 5` total',
          value: p.trimming?.trimmed_5prime_total,
        },
        {
          label: 'Loss extracted to trimmed',
          value: p.trimming?.loss_extracted_to_trimmed,
        },
        {
          label: 'Trimmed percentage',
          value: p.trimming?.trimmed_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
      ]),
      state: progressSectionState(p.trimming?.phase_status),
    },
    {
      key: 'map_human',
      title: 'Mapping on Human Genome',
      items: rows([
        { label: 'Mapped total', value: p.mapping_human?.mapped_total },
        {
          label: 'Loss',
          value: p.mapping_human?.loss_trimmed_to_mapped,
        },
        {
          label: 'Mapped percentage',
          value: p.mapping_human?.mapped_total_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
      ]),
      state: progressSectionState(p.mapping_human?.phase_status),
    },
    {
      key: 'count_human',
      title: 'Counts (Human)',
      items: rows([
        {
          label: 'Assigned total',
          value: p.counts_human?.counts_assigned,
        },
        {
          label: 'Loss mapped to counts',
          value: p.counts_human?.loss_mapped_to_counts,
        },
        {
          label: 'Assigned percentage',
          value: p.counts_human?.counts_assigned_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
        {
          label: 'Hits total',
          value: p.counts_human?.hits_total,
        },
        {
          label: 'Loss counts to hits',
          value: p.counts_human?.loss_counts_to_hits,
        },
        {
          label: 'Hits percentage',
          value: p.counts_human?.hits_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
      ]),
      state: progressSectionState(p.counts_human?.phase_status),
    },
    {
      key: 'filter_out_fakes_human',
      title: 'Filtered fakes (Human)',
      items: rows([
        {
          label: 'Filter hits total',
          value:
            p.filter_out_fakes_human?.umi_tools_hits_log
              ?.fake_filtered_out_hits_total,
        },
        {
          label: 'Filter hits percentage',
          value:
            p.filter_out_fakes_human?.umi_tools_hits_log
              ?.fake_filtered_out_hits_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
        {
          label: 'Threshold barcodes total',
          value:
            p.filter_out_fakes_human?.hits_to_ann_data_threshold
              ?.barcodes_total,
        },
        {
          label: 'Threshold barcodes with 50+ UMIs',
          value:
            p.filter_out_fakes_human?.hits_to_ann_data_threshold?.barcodes_kept,
        },
        {
          label: 'Threshold UMIs yield from 50+ UMIs barcode',
          value:
            p.filter_out_fakes_human?.hits_to_ann_data_threshold
              ?.umi_yield_pct_threshold,
        },
      ]),
      state: progressSectionState(p.filter_out_fakes_human?.phase_status),
    },
    {
      key: 'mapping_bioparticles',
      title: 'Mapping on Bioparticle Genome',
      items: rows([
        {
          label: 'Mapped total',
          value: p.mapping_bioparticles?.mapped_total,
        },
        {
          label: 'Loss',
          value: p.mapping_bioparticles?.loss_trimmed_to_mapped,
        },
        {
          label: 'Mapped percentage',
          value: p.mapping_bioparticles?.mapped_total_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
      ]),
      state: progressSectionState(p.mapping_bioparticles?.phase_status),
    },
    {
      key: 'counts_bioparticles',
      title: 'Counts (Bioparticles)',
      items: rows([
        {
          label: 'Assigned total',
          value: p.counts_bioparticles?.counts_assigned,
        },
        {
          label: 'Loss mapped to counts',
          value: p.counts_bioparticles?.loss_mapped_to_counts,
        },
        {
          label: 'Assigned percentage',
          value: p.counts_bioparticles?.counts_assigned_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
        {
          label: 'Hits total',
          value: p.counts_bioparticles?.hits_total,
        },
        {
          label: 'Loss counts to hits',
          value: p.counts_bioparticles?.loss_counts_to_hits,
        },
        {
          label: 'Hits percentage',
          value: p.counts_bioparticles?.hits_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
      ]),
      state: progressSectionState(p.counts_bioparticles?.phase_status),
    },
  ];
}

function formatProgressRow(
  row: ProgressRow,
  locale = navigator.language || 'en-US'
): string {
  const val =
    typeof row.value === 'number'
      ? row.value
      : Number.isFinite(Number(row.value))
      ? Number(row.value)
      : (row.value as any) ?? '';

  // default scale and suffix
  const scale = row.scale ?? 1;
  const suffix = row.suffix ?? '';

  // non-numeric → show raw
  if (typeof val !== 'number') {
    return `${val}${suffix}`;
  }

  // numeric → show formatted
  const text = new Intl.NumberFormat(locale, row.format).format(val * scale);
  return `${text}${suffix}`;
}

const progressFields = [
  { key: 'label', label: 'Metric' },
  { key: 'value', label: 'Value', tdClass: 'text-end font-monospace' },
];

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _directive_b_tooltip = _resolveDirective("b-tooltip")!

  return (props.task?.id)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(_unref(BCard), {
          ref_key: "cardRef",
          ref: cardRef,
          class: _normalizeClass([
        'd-flex flex-column p-0 card-sample',
        isExpanded.value ? 'expanded' : 'collapsed h-100',
      ])
        }, {
          default: _withCtx(() => [
            _renderSlot(_ctx.$slots, "default", {}, () => [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _withDirectives((_openBlock(), _createElementBlock("h2", {
                    onClick: toggleCard,
                    id: `task-title-${props.task.id}`,
                    class: "mb-0 task-title",
                    title: isExpanded.value ? 'Click to collapse' : 'Click to expand'
                  }, [
                    (!isExpanded.value)
                      ? (_openBlock(), _createBlock(ComputeTaskStatusBadge, {
                          key: 0,
                          task: props.task,
                          "icon-only": ""
                        }, null, 8, ["task"]))
                      : _createCommentVNode("", true),
                    _createTextVNode(" " + _toDisplayString(props.task.name) + " ", 1),
                    _createVNode(_unref(BBadge), {
                      variant: "secondary",
                      class: "ms-2"
                    }, {
                      default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                        _createElementVNode("small", { style: {"font-weight":"lighter"} }, [
                          _createElementVNode("i", { class: "fas fa-archive" }),
                          _createTextVNode(" Legacy ")
                        ], -1)
                      ]))]),
                      _: 1
                    }),
                    (isExpanded.value)
                      ? (_openBlock(), _createBlock(_component_font_awesome_icon, {
                          key: 1,
                          icon: "chevron-up",
                          class: "ms-2"
                        }))
                      : _createCommentVNode("", true)
                  ], 8, _hoisted_4)), [
                    [
                      _directive_b_tooltip,
                      void 0,
                      void 0,
                      { hover: true }
                    ]
                  ]),
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(ComputeTaskStatusBadge, {
                      task: props.task
                    }, null, 8, ["task"])
                  ])
                ]),
                _createElementVNode("div", _hoisted_6, [
                  (!isExpanded.value)
                    ? (_openBlock(), _createBlock(_unref(BButton), {
                        key: 0,
                        onClick: expandCard,
                        variant: "link",
                        size: "sm",
                        class: "p-0 m-0 link-secondary text-decoration-none"
                      }, {
                        default: _withCtx(() => [
                          _cache[21] || (_cache[21] = _createTextVNode(" Show More ", -1)),
                          _createVNode(_component_font_awesome_icon, {
                            icon: "chevron-down",
                            class: "ms-1"
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (isExpanded.value)
                    ? (_openBlock(), _createBlock(_unref(BButtonGroup), { key: 1 }, {
                        default: _withCtx(() => [
                          (props.task.bucket)
                            ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                                key: 0,
                                size: "sm",
                                variant: "outline-dark",
                                onClick: _cache[0] || (_cache[0] = ($event: any) => {
                  expandCard();
                  selectTab(_unref(DATA_PIPELINE_TASK_TAB_OPTIONS).PROGRESS);
                }),
                                id: `btn-progress-${props.task.id}`,
                                title: "View progress timeline with stats",
                                active: 
                  isExpanded.value &&
                  selectedTab.value === _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).PROGRESS
                
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_font_awesome_icon, { icon: "list-check" })
                                ]),
                                _: 1
                              }, 8, ["id", "active"])), [
                                [
                                  _directive_b_tooltip,
                                  void 0,
                                  void 0,
                                  { hover: true }
                                ]
                              ])
                            : _createCommentVNode("", true),
                          (props.task.bucket)
                            ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                                key: 1,
                                hidden: "",
                                size: "sm",
                                variant: "outline-dark",
                                onClick: _cache[1] || (_cache[1] = ($event: any) => {
                  expandCard();
                  selectTab(_unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FIGURES);
                }),
                                id: `btn-figures-${props.task.id}`,
                                title: "View figures",
                                active: 
                  isExpanded.value &&
                  selectedTab.value === _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FIGURES
                
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_font_awesome_icon, { icon: "chart-line" })
                                ]),
                                _: 1
                              }, 8, ["id", "active"])), [
                                [
                                  _directive_b_tooltip,
                                  void 0,
                                  void 0,
                                  { hover: true }
                                ]
                              ])
                            : _createCommentVNode("", true),
                          (props.task.bucket)
                            ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                                key: 2,
                                size: "sm",
                                variant: "outline-dark",
                                onClick: _cache[2] || (_cache[2] = ($event: any) => {
                  expandCard();
                  selectTab(_unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FILES);
                }),
                                id: `btn-files-${props.task.id}`,
                                title: "View files",
                                active: 
                  isExpanded.value &&
                  selectedTab.value === _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FILES
                
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_font_awesome_icon, { icon: "file" })
                                ]),
                                _: 1
                              }, 8, ["id", "active"])), [
                                [
                                  _directive_b_tooltip,
                                  void 0,
                                  void 0,
                                  { hover: true }
                                ]
                              ])
                            : _createCommentVNode("", true),
                          _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                            variant: "outline-dark",
                            size: "sm",
                            onClick: _cache[3] || (_cache[3] = ($event: any) => (emits('exec-details-requested'))),
                            id: `btn-exec-${props.task.id}`,
                            title: "View exec configuration"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_font_awesome_icon, { icon: ['fas', 'sliders'] })
                            ]),
                            _: 1
                          }, 8, ["id"])), [
                            [
                              _directive_b_tooltip,
                              void 0,
                              void 0,
                              { hover: true }
                            ]
                          ]),
                          (_unref(IS_DEBUG_MODE) || _unref(userStore).developer)
                            ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                                key: 3,
                                variant: "outline-dark",
                                size: "sm",
                                onClick: _cache[4] || (_cache[4] = ($event: any) => (emits('bucket-requested'))),
                                id: `btn-bucket-${props.task.id}`,
                                disabled: !props.task.bucket,
                                title: "Go to data"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_font_awesome_icon, { icon: ['fas', 'database'] })
                                ]),
                                _: 1
                              }, 8, ["id", "disabled"])), [
                                [
                                  _directive_b_tooltip,
                                  void 0,
                                  void 0,
                                  { hover: true }
                                ]
                              ])
                            : _createCommentVNode("", true),
                          (_unref(IS_DEBUG_MODE) || _unref(userStore).developer)
                            ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                                key: 4,
                                variant: "outline-dark",
                                size: "sm",
                                onClick: _cache[5] || (_cache[5] = ($event: any) => (emits('debug-details-requested'))),
                                id: `btn-debug-${props.task.id}`,
                                title: "View debug details"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_font_awesome_icon, { icon: ['fas', 'code'] })
                                ]),
                                _: 1
                              }, 8, ["id"])), [
                                [
                                  _directive_b_tooltip,
                                  void 0,
                                  void 0,
                                  { hover: true }
                                ]
                              ])
                            : _createCommentVNode("", true),
                          _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                            size: "sm",
                            variant: "outline-danger",
                            disabled: !_unref(ComputeTaskUtils).canBeAborted(props.task.status),
                            onClick: _cache[6] || (_cache[6] = ($event: any) => (emits('abort-requested'))),
                            id: `btn-abort-${props.task.id}`,
                            title: "Abort task",
                            active: props.task.status === _unref(COMPUTE_TASK_STATUS).ABORTING
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_font_awesome_icon, { icon: "times" })
                            ]),
                            _: 1
                          }, 8, ["disabled", "id", "active"])), [
                            [
                              _directive_b_tooltip,
                              void 0,
                              void 0,
                              { hover: true }
                            ]
                          ])
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ])
              ]),
              _createVNode(_unref(BCollapse), {
                modelValue: isExpanded.value,
                "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((isExpanded).value = $event)),
                class: "mt-2 flex-grow-1",
                onShow: _cache[16] || (_cache[16] = ($event: any) => (emitLayout('start'))),
                onHide: _cache[17] || (_cache[17] = ($event: any) => (emitLayout('start'))),
                onShown: _cache[18] || (_cache[18] = ($event: any) => (emitLayout('end'))),
                onHidden: _cache[19] || (_cache[19] = ($event: any) => (emitLayout('end')))
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.task.results?.samples || [], (sample, sid) => {
                    return (_openBlock(), _createElementBlock("div", { key: sid }, [
                      (sample.name && props.task.results?.samples.length > 1)
                        ? (_openBlock(), _createElementBlock("h3", _hoisted_7, " Sample " + _toDisplayString(sample.name), 1))
                        : _createCommentVNode("", true),
                      (
                sample.report.progress &&
                selectedTab.value === _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).PROGRESS
              )
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _withDirectives((_openBlock(), _createElementBlock("h3", {
                              onClick: _cache[7] || (_cache[7] = ($event: any) => (detailedView.value = !detailedView.value)),
                              style: {"cursor":"pointer"},
                              title: detailedView.value ? 'Click to collapse' : 'Click to expand',
                              id: `progress-title-${props.task.id}`
                            }, [
                              _cache[22] || (_cache[22] = _createTextVNode(" Progress ", -1)),
                              _createElementVNode("i", {
                                class: _normalizeClass(
                    detailedView.value
                      ? ['ms-1', 'bi', 'bi-arrows-collapse']
                      : ['ms-1', 'bi', 'bi-arrows-expand']
                  )
                              }, null, 2)
                            ], 8, _hoisted_8)), [
                              [
                                _directive_b_tooltip,
                                void 0,
                                void 0,
                                { hover: true }
                              ]
                            ]),
                            _createElementVNode("div", _hoisted_9, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(taskProgress(sample), (sec, i) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: sec.key || i,
                                  class: "route__section"
                                }, [
                                  _createElementVNode("div", {
                                    class: _normalizeClass(["route__marker", sec.state])
                                  }, [
                                    _createElementVNode("span", _hoisted_10, "ProgressSection " + _toDisplayString(i + 1), 1)
                                  ], 2),
                                  _createElementVNode("div", _hoisted_11, [
                                    _createElementVNode("h5", _hoisted_12, _toDisplayString(sec.title), 1),
                                    _createVNode(_unref(BCollapse), {
                                      modelValue: detailedView.value,
                                      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((detailedView).value = $event)),
                                      onShow: _cache[9] || (_cache[9] = ($event: any) => (emitLayout('start'))),
                                      onHide: _cache[10] || (_cache[10] = ($event: any) => (emitLayout('start'))),
                                      onShown: _cache[11] || (_cache[11] = ($event: any) => (emitLayout('end'))),
                                      onHidden: _cache[12] || (_cache[12] = ($event: any) => (emitLayout('end')))
                                    }, {
                                      default: _withCtx(() => [
                                        (sec.items?.length)
                                          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                              _createVNode(_unref(BTable), {
                                                items: sec.items,
                                                fields: progressFields,
                                                small: "",
                                                striped: "",
                                                hover: "",
                                                responsive: "sm",
                                                stacked: "sm",
                                                class: "browser-table route__table mb-0",
                                                "thead-class": "visually-hidden"
                                              }, {
                                                "cell(label)": _withCtx(({ item }) => [
                                                  _createTextVNode(_toDisplayString(item.label), 1)
                                                ]),
                                                "cell(value)": _withCtx(({ item }) => [
                                                  _createTextVNode(_toDisplayString(formatProgressRow(item)), 1)
                                                ]),
                                                _: 1
                                              }, 8, ["items"])
                                            ]))
                                          : _createCommentVNode("", true)
                                      ]),
                                      _: 2
                                    }, 1032, ["modelValue"])
                                  ])
                                ]))
                              }), 128))
                            ])
                          ], 64))
                        : _createCommentVNode("", true),
                      (selectedTab.value === _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FIGURES)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_14, [...(_cache[23] || (_cache[23] = [
                            _createElementVNode("h3", null, "Figures", -1),
                            _createElementVNode("p", null, "Figure details go here...", -1)
                          ]))]))
                        : _createCommentVNode("", true),
                      (selectedTab.value === _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FILES)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                            _createVNode(ComputeTaskFilesList, {
                              files: sample.report.files,
                              onFilePreviewRequested: _cache[13] || (_cache[13] = ($event: any) => (
                  emits('file-preview-requested', $event)
                )),
                              onFileDownloadRequested: _cache[14] || (_cache[14] = ($event: any) => (
                  emits('file-download-requested', $event)
                ))
                            }, null, 8, ["files"])
                          ]))
                        : _createCommentVNode("", true)
                    ]))
                  }), 128))
                ]),
                _: 1
              }, 8, ["modelValue"])
            ])
          ]),
          _: 3
        }, 8, ["class"])
      ]))
    : _createCommentVNode("", true)
}
}

})